import Button from "@components/Button";
import {Translate} from "next-translate";
import {useTranslation} from "ni18n";
import React, {useMemo} from "react";

import {testColors} from "../../../constants/Reason/getCareDiscoveryColorScheme";
import useMediaQuery from "../../../useMediaQuery";
import {CareInsuranceType} from "../../../utils/cms/types";
import {transparent} from "../../../utils/transparent";

interface WidthVariant {
  isFullWidth: boolean;
}

const Header: React.FC<{text: string; reduceBottomPadding: boolean} & WidthVariant> = ({
  text,
  isFullWidth,
  reduceBottomPadding,
}) =>
  React.createElement(
    isFullWidth ? "h1" : "strong",
    {
      className: `font-csb white fsn ls-25 lh12 ${
        isFullWidth
          ? "fs48 fs28-lg fs24-sm tac tal-lg ph12 ph0-lg pb18 pb10-lg pb8-sm"
          : reduceBottomPadding
          ? "fs40 fs30-md fs24-sm pb0"
          : "fs40 fs30-md fs24-sm pb10 pb8-sm"
      }`,
    },
    text,
  );

const InsuranceList: React.FC<WidthVariant> = ({isFullWidth}) => {
  const i18n = useTranslation();
  const isMinMd = useMediaQuery("md", {min: true});

  const insuranceItems = useMemo(
    () => [
      {label: "Aetna", icon: `ins-aetna`},
      {label: "Blue Cross Blue Shield", icon: `ins-blue-cross-blue-shield`},
      {label: "Cigna", icon: `ins-cigna fs38-f fs24-f-sm`},
      {label: "Humana", icon: `ins-humana fs26-f fs24-f-sm`},
      {label: "Medicare", icon: `ins-medicare fs26-f fs16-f-sm`},
      {
        label: "Sutter Health",
        icon: `ins-sutter-health fs40`,
        subtext: i18n.t("* Urgent Care Only"),
      },
      {label: "Tricare", icon: `ins-tricare fs50 fs30-sm`},
      {label: "United", icon: `ins-united fs32`},
    ],
    [i18n],
  );

  return (
    <ul
      className={`dg gg3 ${isFullWidth ? "gafr gtc3fr gtc2fr-sm pb18 pb10-lg" : "af"}`}
      style={isFullWidth ? {} : {"--w": isMinMd ? "180px" : "140px"}}
    >
      {insuranceItems.map(item => (
        <li
          key={item.icon}
          className="df fdc aic jcc pv10 pv2-sm tac white br1 contrast-tb"
          style={{
            maxHeight: "7rem",
            minHeight: "3.125rem",
            backgroundColor: transparent("#ffffff", 10),
          }}
        >
          <span className={`ma mv1 fs30 fs20-sm cIcon-${item.icon}`} aria-hidden />
          <span className="visually-hidden">{item.label}</span>
          {item.subtext && <strong className="wsnw ma mt2 fs10 fs9-sm fsn">{item.subtext}</strong>}
        </li>
      ))}
    </ul>
  );
};

const CallPrompt: React.FC<WidthVariant> = ({isFullWidth}) => {
  const i18n = useTranslation();

  return (
    <div
      className={`font-cm fs24 lh13 ls-3 fs18-sm flex justify-center items-center gap-4 pb10 ${
        isFullWidth ? "tac tal-lg" : "mt10 "
      }`}
    >
      <span style={{color: testColors.lightBlue}}>
        {i18n.t("Contact your insurance company to check your coverage.")}
      </span>
      <Button href="#faq" size="sm" variant="white" className="">
        {i18n.t("Learn More")}
      </Button>
    </div>
  );
};

export const getCareInsuranceText = (
  t: Translate,
  insuranceType?: CareInsuranceType,
  contentTitle = "this",
): string => {
  switch (insuranceType) {
    case CareInsuranceType.OccupationalHealth:
      return t(
        "If your employer has sent you to Carbon Health for a workplace health visit, the cost is typically covered by your employer or reimbursed afterwards.",
      );
    case CareInsuranceType.Standard:
      return t(
        "We accept most major insurance plans. No insurance? No problem, our pricing is clear and affordable.",
      );
    case CareInsuranceType.Medicaid:
      return t(
        "The only insurance we accept for {{conditionSymtomOrTopic}} is Medicaid. No Medicaid? No problem, our pricing is clear and affordable.",
        {
          conditionSymtomOrTopic: contentTitle,
        },
      );
    case CareInsuranceType.NoInsurance:
      return t("Insurance is not accepted for {{conditionSymtomOrTopic}}.", {
        conditionSymtomOrTopic: contentTitle,
      });
    default:
      return getCareInsuranceText(t, CareInsuranceType.Standard);
  }
};
export interface InsurancesProps extends WidthVariant {
  headerText: string;
  insuranceType?: CareInsuranceType;
}

export const Insurances: React.FC<InsurancesProps> = ({headerText, insuranceType, isFullWidth}) => {
  const i18n = useTranslation();

  /**
   * This component is used as hero at /insurance-pricing, where no insuranceType is present.
   * Allow logos on that page ->
   */
  const hideLogos = insuranceType && insuranceType !== CareInsuranceType.Standard;

  return React.createElement(
    isFullWidth ? "div" : "section",
    {
      id: "insurance-and-pricing",
      className: `df fdc ${isFullWidth ? "maxw70 ma ph8-xl ph16-lg ph5-sm" : ""}`,
      "aria-label": isFullWidth ? null : i18n.t("Insurance and Pricing"),
    },
    <>
      {!isFullWidth && (
        <h2 className="font-mb ttu white fs12 pb4">{i18n.t("Insurance and pricing")}</h2>
      )}
      {/* @ts-expect-error TS2322: Type 'boolean | undefined' is not assignable to type 'boolean'. */}
      <Header isFullWidth={isFullWidth} text={headerText} reduceBottomPadding={hideLogos} />
      <CallPrompt isFullWidth={isFullWidth} />

      {!hideLogos && <InsuranceList isFullWidth={isFullWidth} />}
    </>,
  );
};

export default Insurances;
